import React, { useState } from 'react';
import { MapPin, Phone, Mail, Building2, ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';

const ContactPage = () => {
  usePageTitle(
    `Contact ${BRAND_INFO.name}`,
    `Get in touch with the ${BRAND_INFO.name} team. Email, phone, and mailing address for ${CONTACT_INFO.corporateName}.`
  );

  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', topic: 'order', message: '' });

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setForm({ name: '', email: '', topic: 'order', message: '' });
  };

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-12 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">Help</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900 max-w-3xl">
            We answer email like real{' '}
            <em className="italic text-brick-500">humans</em>.
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl">
            Hit us up about an order, a missing result, a refund request, or anything
            account-related. Most replies go out within one business day.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-cream-100">
        <div className="container-wide grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="bg-cream-50 border border-cream-200 rounded-3xl p-8 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.16em] text-ink-500 mb-1.5">
                    Name
                  </label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-cream-300"
                  />
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-[0.16em] text-ink-500 mb-1.5">
                    Email
                  </label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-cream-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-[0.16em] text-ink-500 mb-1.5">
                  Topic
                </label>
                <select
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-cream-300"
                >
                  <option value="order">Order or panel question</option>
                  <option value="results">Results or dashboard issue</option>
                  <option value="refund">Refund or cancellation</option>
                  <option value="upload">PDF upload help</option>
                  <option value="press">Press / partnerships</option>
                  <option value="other">Something else</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-[0.16em] text-ink-500 mb-1.5">
                  Message
                </label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-cream-300 resize-none"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 bg-brick-500 hover:bg-brick-600 text-white font-medium px-7 py-3.5 rounded-full transition-colors"
              >
                Send message <ArrowRight className="w-4 h-4" />
              </button>
              {submitted && (
                <p className="text-sage-700 text-[14px]">
                  Thanks — we received your message and will be in touch shortly.
                </p>
              )}
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-cream-50 border border-cream-200 rounded-3xl p-7">
              <h3 className="font-display text-2xl text-ink-900">Reach us directly</h3>
              <ul className="mt-5 space-y-4 text-[15px]">
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-brick-500" />
                  <a href={`mailto:${CONTACT_INFO.email}`} className="text-ink-900 hover:text-brick-500">
                    {CONTACT_INFO.email}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 mt-1 text-brick-500" />
                  <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-ink-900 hover:text-brick-500">
                    {CONTACT_INFO.phone}
                  </a>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-brick-500" />
                  <span className="text-ink-800">
                    {CONTACT_INFO.address.street}<br />
                    {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                    {CONTACT_INFO.address.country}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 mt-1 text-brick-500" />
                  <span className="text-ink-800">
                    Operated by {CONTACT_INFO.corporateName}
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-charcoal-900 text-cream-50 rounded-3xl p-7">
              <h3 className="font-display text-2xl">Hours</h3>
              <p className="mt-3 text-cream-200/80">
                Customer support is staffed Monday through Friday, 9am to 6pm Central time.
                Email is answered seven days a week and is the fastest way to reach us.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ContactPage;
